import {
  Card,
  CardHeader,
  CardFooter,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Table,
  Container,
  Row,
  Button,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { getConditionDataList } from "services/_conditions";
import moment from "moment";
import { deleteConditionDataById } from "services/_conditions";
import { errorToast } from "components/toast";
import { successToast } from "components/toast";
import { FormControlLabel, Switch } from "@mui/material";
import { updateConditionStatus } from "services/_conditions";
import Pagination from "components/Pagination";
import { updateMedicalConditionSortOrders } from "services/_conditions";
import { getPermissions } from "context/AuthContext";

const MedicalCondition = () => {
  const permissions = getPermissions();

  const navigate = useNavigate();
  const [conditionData, setConditionData] = useState([]);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortOrder, setSortOrder] = useState({});
  const createCondition = () => {
    navigate("/admin/medical-condition/create-medical-condition");
  };

  const initialData = useCallback(async () => {
    const data = await getConditionDataList(`page=${page}&per_page=${perPage}`);
    if (data.data) {
      setRecords(data.data.records || []);
      setConditionData(data.data.records);
      setTotalRecords(data.data.total_records || 0);

      if (data.data.total_records && perPage) {
        setTotalPages(Math.ceil(data.data.total_records / perPage));
      }
    }
  }, [page, perPage]);

  const handleSortOrderChange = (id, value) => {
    setSortOrder((prev) => ({
      ...prev,
      [id]: Number(value),
    }));
  };

  const handleSaveSortOrders = async () => {
    try {
      if (!Object.keys(sortOrder).length) {
        return errorToast("No sort orders changed");
      }

      // Prepare payload for API
      const payload = Object.entries(sortOrder).map(([id, sort_order]) => ({
        id,
        sort_order: Number(sort_order),
      }));

      if (payload.length === 0) {
        return errorToast("No sort orders to update");
      }

      // Call API to update sort orders in DB
      const response = await updateMedicalConditionSortOrders(payload);
      console.log("Update Sort Orders Response:", response);

      if (response?.isSuccess) {
        successToast(response.message);

        // Reset sortOrder input state
        setSortOrder({});

        // Refresh data from DB to reflect changes
        initialData();
      } else {
        errorToast(response.message || "Failed to update sort orders");
      }
    } catch (err) {
      console.error(err);
      errorToast("Failed to update sort orders");
    }
  };

  useEffect(() => {
    initialData();
  }, [initialData]);

  const handleEdit = async (id) => {
    navigate(`/admin/medical-condition/edit-medical-condition/${id}`);
  };

  const handleDelete = async (id) => {
    const data = await deleteConditionDataById(id);
    if (data.data) {
      successToast(data.message);
      initialData();
    } else {
      errorToast(data.message);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      // Call your API to update the status
      const payload = { id, status: newStatus };
      const response = await updateConditionStatus(payload);

      if (response.data) {
        successToast(response.message);

        setConditionData((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, status: newStatus } : item
          )
        );
      } else {
        errorToast(response.message);
      }
    } catch (err) {
      console.error(err);
      errorToast("Something went wrong!");
    }
  };

  const prevButton = async () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const nextButton = async () => {
    if (totalPages > page) {
      setPage(page + 1);
    }
  };

  return (
    <>
      <Header />
      {/* Page content */}
      <Container className="mt--7" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between">
                <h3>Medical Condition</h3>
                {permissions.includes("create-medical-condition") && (
                  <Button color="info" onClick={createCondition}>
                    Create
                  </Button>
                )}
              </CardHeader>
              <Table
                className="align-items-center table-flush"
                responsive
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead className="thead-light">
                  <tr>
                    <th
                      scope="col"
                      style={{
                        width: "50px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      #
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: "100px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      Sort Order
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: "220px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Name
                    </th>
                    <th scope="col" className="blog-th-short-desc">
                      Short Description
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: "100px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Created At
                    </th>
                    {permissions.includes(
                      "update-medical-condition" || "delete-medical-condition"
                    ) && (
                      <th
                        scope="col"
                        style={{
                          width: "100px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                      />
                    )}
                  </tr>
                </thead>
                <tbody>
                  {conditionData.length > 0 ? (
                    conditionData.map((condition, index) => {
                      return (
                        <>
                          <tr key={index}>
                            <th
                              scope="row"
                              style={{
                                width: "50px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                              }}
                            >
                              {" "}
                              {(page - 1) * perPage + index + 1}
                            </th>
                            <td
                              style={{
                                width: "100px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                              }}
                            >
                              <input
                                className="form-control-alternative"
                                type="text"
                                value={
                                  sortOrder[condition.id] !== undefined
                                    ? sortOrder[condition.id] // if admin has typed something
                                    : condition.sort_order || "" // otherwise show DB value
                                }
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^\d*$/.test(val)) {
                                    handleSortOrderChange(
                                      condition.id,
                                      val ? Number(val) : ""
                                    );
                                  }
                                }}
                                style={{
                                  width: "40px",
                                  textAlign: "center",
                                  padding: "5px",
                                  borderRadius: "0.50rem",
                                }}
                              />
                            </td>

                            <td
                              style={{
                                width: "220px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {condition.name}
                            </td>
                            <td className="blog-th-short-desc">
                              {condition.short_description}
                            </td>
                            <td
                              style={{
                                width: "100px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={condition.status}
                                    onChange={() =>
                                      handleStatusChange(
                                        condition.id,
                                        !condition.status
                                      )
                                    }
                                  />
                                }
                              />
                            </td>
                            <td
                              style={{
                                width: "150px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {condition.created_at
                                ? moment(condition.created_at).format(
                                    "DD MMMM, YYYY"
                                  )
                                : "-"}
                            </td>
                            {permissions.includes(
                              "update-medical-condition" ||
                                "delete-medical-condition"
                            ) && (
                              <td className="text-center">
                                <UncontrolledDropdown>
                                  <DropdownToggle
                                    className="btn-icon-only text-light"
                                    href="#pablo"
                                    role="button"
                                    size="sm"
                                    color=""
                                    onClick={(e) => e.preventDefault()}
                                  >
                                    <i className="fas fa-ellipsis-v" />
                                  </DropdownToggle>
                                  <DropdownMenu
                                    className="dropdown-menu-arrow"
                                    right
                                  >
                                    {permissions.includes(
                                      "update-medical-condition"
                                    ) && (
                                      <DropdownItem
                                        onClick={() => handleEdit(condition.id)}
                                      >
                                        <i
                                          className="fas fa-edit"
                                          style={{
                                            color: "#1976d2",
                                            cursor: "pointer",
                                            marginRight: "10px",
                                          }}
                                        ></i>
                                        Edit
                                      </DropdownItem>
                                    )}
                                    {permissions.includes(
                                      "delete-medical-condition"
                                    ) && (
                                      <DropdownItem
                                        onClick={() =>
                                          handleDelete(condition.id)
                                        }
                                      >
                                        <i
                                          className="fas fa-trash"
                                          style={{
                                            color: "red",
                                            cursor: "pointer",
                                            marginRight: "10px",
                                          }}
                                        ></i>
                                        Delete
                                      </DropdownItem>
                                    )}
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            )}
                          </tr>
                        </>
                      );
                    })
                  ) : (
                    <tr>
                      <td>No record found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <CardFooter className="py-4">
                <Button color="info" onClick={handleSaveSortOrders}>
                  Update Sort
                </Button>
                <Pagination
                  page={page}
                  perPage={perPage}
                  totalRecords={totalRecords}
                  totalPages={totalPages}
                  records={records}
                  prevButton={prevButton}
                  nextButton={nextButton}
                />
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default MedicalCondition;
